import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchLayaways,
  fetchLayaway,
  createLayaway,
  updateLayaway,
  addLayawayPayment,
  cancelLayaway,
  deleteLayaway,
  fetchItems,
  fetchInventory,
  fetchUsers,
  fetchBranches,
  fetchSettings,
} from '@/api';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ShoppingCart,
  Search,
  Plus,
  Eye,
  DollarSign,
  Calendar,
  User,
  Package,
  Trash2,
  Ban,
  Minus,
  Filter,
  Grid3X3,
  List,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Sparkles,
  CreditCard,
  Banknote,
  Smartphone,
  Building2,
  Settings,
  FileText,
  Printer,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { debounce } from 'lodash';
import { CreateLayawayTab } from '@/components/layaways/CreateLayawayTab';
import { LayawayInvoice } from '@/components/layaways/LayawayInvoice';
import { EditLayawayDialog } from '@/components/layaways/EditLayawayDialog';
import { Edit } from 'lucide-react';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { DateTimePicker } from '@/components/ui/datetime-picker';

interface Layaway {
  id: string;
  layaway_number: number;
  customer_name: string;
  customer_phone?: string;
  customer_address?: string;
  total_amount: number;
  paid_amount: number;
  remaining_amount: number;
  status: 'active' | 'completed' | 'cancelled';
  product_handling: 'hold' | 'handover';
  payment_schedule_months: number;
  created_at: string;
  days_active?: number;
  balance_due?: number;
  items?: LayawayItem[];
  payments?: LayawayPayment[];
}

interface LayawayItem {
  id: string;
  item_id: string;
  name: string;
  sku: string;
  quantity: number;
  price: number;
}

interface LayawayPayment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  notes?: string;
}

interface Item {
  id: string;
  name: string;
  sku: string;
  selling_price: string | number;
  quantity: number;
  barcode?: string;
  vat_enabled?: number;
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  sku: string;
  vat_enabled?: number;
}

const Layaways: React.FC = () => {
  useEffect(() => {
    document.title = 'Layaways - Vixalor';
  }, []);
  const [layaways, setLayaways] = useState<Layaway[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [activeTab, setActiveTab] = useState('list');
  
  // Create Layaway states
  const [items, setItems] = useState<Item[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    address: '',
  });
  const [productHandling, setProductHandling] = useState<'hold' | 'handover'>('hold');
  const [paymentScheduleMonths, setPaymentScheduleMonths] = useState(1);
  const [initialPayment, setInitialPayment] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [vatPercentage, setVatPercentage] = useState<number>(0);
  const [systemTimezone, setSystemTimezone] = useState<string>('Africa/Windhoek');
  // Current date in system timezone (not UTC)
  const [layawayDate, setLayawayDate] = useState<Date>(() => {
    const now = toZonedTime(new Date(), 'Africa/Windhoek');
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const currencySymbol = useCurrencySymbol();
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Vixalor POS',
    address: '123 Business Street',
    phone: '+1234567890',
    email: 'info@vixalor.com'
  });

  // Enhanced UI states
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemViewMode, setItemViewMode] = useState<'grid' | 'list'>('grid');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [isCreating, setIsCreating] = useState(false);
  const [isAddingPayment, setIsAddingPayment] = useState(false);

  // View/Payment Dialog states
  const [selectedLayaway, setSelectedLayaway] = useState<Layaway | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLayaway, setEditingLayaway] = useState<Layaway | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethodDialog, setPaymentMethodDialog] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  // Branch selection
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Calculate available quantities for items based on selected branch
  const itemsWithAvailability = useMemo(() => {
    return items.map(item => {
      // Get inventory records for this item in the selected branch
      const branchInventory = inventory.filter(inv => 
        inv.item_id === item.id && 
        (selectedBranch ? inv.branch_id === selectedBranch : true)
      );
      
      // Calculate total available quantity (using available_quantity if present, otherwise quantity)
      const totalAvailable = branchInventory.reduce((sum, inv) => {
        const available = inv.available_quantity !== undefined ? inv.available_quantity : inv.quantity;
        return sum + parseInt(available || 0, 10);
      }, 0);
      
      return {
        ...item,
        availableQuantity: totalAvailable,
        originalQuantity: item.quantity
      };
    });
  }, [items, inventory, selectedBranch]);

  // Enhanced item filtering and sorting
  const filteredAndSortedItems = useMemo(() => {
    let filtered = itemsWithAvailability;

    // Search filter
    if (itemSearchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        (item.barcode && item.barcode.includes(itemSearchTerm))
      );
    }

    // Sort items
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return parseFloat(a.selling_price.toString()) - parseFloat(b.selling_price.toString());
        case 'stock':
          return b.availableQuantity - a.availableQuantity;
        default:
          return 0;
      }
    });

    return filtered;
  }, [itemsWithAvailability, itemSearchTerm, categoryFilter, sortBy]);

  const cartSubtotal = useMemo(() => {
    return cart.reduce((total, item) => total + (parseFloat(item.price.toString()) * item.quantity), 0);
  }, [cart]);

  const cartVAT = useMemo(() => {
    return cart.reduce((total, item) => {
      if (item.vat_enabled) {
        const itemTotal = parseFloat(item.price.toString()) * item.quantity;
        return total + (itemTotal * (vatPercentage / 100));
      }
      return total;
    }, 0);
  }, [cart, vatPercentage]);

  const cartTotal = useMemo(() => {
    return cartSubtotal + cartVAT;
  }, [cartSubtotal, cartVAT]);

  const remainingBalance = useMemo(() => {
    return Math.max(0, cartTotal - initialPayment);
  }, [cartTotal, initialPayment]);

  const monthlyPayment = useMemo(() => {
    return paymentScheduleMonths > 0 ? remainingBalance / paymentScheduleMonths : 0;
  }, [remainingBalance, paymentScheduleMonths]);

  const formatLayawayDate = useCallback((date: string | number | Date) => {
    return formatInTimeZone(
      toZonedTime(new Date(date), 'UTC'),
      systemTimezone,
      'yyyy-MM-dd'
    );
  }, [systemTimezone]);

  const fetchLayawaysData = useCallback(
    debounce(async () => {
      try {
        setLoading(true);
        const response = await fetchLayaways({
          page: pagination.current_page,
          per_page: pagination.per_page,
          search: searchTerm,
          status: statusFilter,
        });
        setLayaways(response.data.data);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error('Failed to fetch layaways:', error);
        toast.error('Failed to fetch layaways');
      } finally {
        setLoading(false);
      }
    }, 300),
    [pagination.current_page, pagination.per_page, searchTerm, statusFilter]
  );

  useEffect(() => {
    fetchLayawaysData();
  }, [fetchLayawaysData]);

  useEffect(() => {
    const loadItemsAndInventory = async () => {
      try {
        const [itemsResponse, inventoryResponse, settingsResponse] = await Promise.all([
          fetchItems(),
          fetchInventory(),
          fetchSettings()
        ]);
        setItems(itemsResponse.data);
        setInventory(inventoryResponse.data);
        setVatPercentage(settingsResponse.data.vat_percentage || 0);
        setSystemTimezone(settingsResponse.data.timezone || 'Africa/Windhoek');
        setCompanySettings({
          company_name: settingsResponse.data.company_name || 'Vixalor POS',
          address: settingsResponse.data.address || '123 Business Street',
          phone: settingsResponse.data.phone || '+1234567890',
          email: settingsResponse.data.email || 'info@vixalor.com'
        });
      } catch (error) {
        console.error('Failed to fetch items or inventory:', error);
      }
    };
    loadItemsAndInventory();
  }, []);

  // Keep layaway date aligned with system timezone changes
  useEffect(() => {
    const now = toZonedTime(new Date(), systemTimezone);
    now.setHours(0, 0, 0, 0);
    setLayawayDate(now);
  }, [systemTimezone]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await fetchBranches();
        setBranches(response.data);
        
        // If not admin, set the user's branch as selected
        if (!isAdmin && user?.branch_id) {
          setSelectedBranch(user.branch_id);
        } else if (isAdmin && response.data.length > 0) {
          // For admin, select the main branch if it exists, otherwise first branch
          const mainBranch = response.data.find(b => b.isMain || b.is_main);
          setSelectedBranch(mainBranch ? mainBranch.id : response.data[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    };
    loadBranches();
  }, [isAdmin, user?.branch_id]);

  // Re-validate cart when branch changes
  useEffect(() => {
    if (!selectedBranch) return;
    
    // Validate cart items against new branch inventory
    if (cart.length > 0) {
      const updatedCart = cart.map(cartItem => {
        const itemWithAvailability = itemsWithAvailability.find(i => i.id === cartItem.id);
        const availableQty = itemWithAvailability?.availableQuantity || 0;
        
        // Adjust quantity if it exceeds available
        if (cartItem.quantity > availableQty) {
          if (availableQty === 0) {
            return null; // Mark for removal
          }
          toast.warning(`${cartItem.name} quantity adjusted from ${cartItem.quantity} to ${availableQty} (max available)`);
          return { ...cartItem, quantity: availableQty };
        }
        return cartItem;
      }).filter(Boolean); // Remove null items
      
      if (updatedCart.length !== cart.length) {
        toast.warning(`Some items were removed from cart as they're not available in the selected branch.`);
      }
      
      if (JSON.stringify(updatedCart) !== JSON.stringify(cart)) {
        setCart(updatedCart);
      }
    }
  }, [selectedBranch, itemsWithAvailability]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value === 'all' ? '' : value);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const addToCart = (item: any) => {
    // Check if we have a selected branch
    if (!selectedBranch && isAdmin) {
      toast.error('Please select a branch first');
      return;
    }
    
    // Get the item with availability info
    const itemWithAvailability = itemsWithAvailability.find(i => i.id === item.id);
    const availableQty = itemWithAvailability?.availableQuantity || 0;
    
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    const currentCartQty = existingItem ? existingItem.quantity : 0;
    
    if (currentCartQty >= availableQty) {
      toast.error(`Cannot add more items. Only ${availableQty} available in selected branch.`);
      return;
    }
    
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      if (availableQty > 0) {
        setCart([...cart, {
          id: item.id,
          name: item.name,
          price: item.selling_price,
          quantity: 1,
          sku: item.sku,
          vat_enabled: item.vat_enabled,
        }]);
      } else {
        toast.error('Item is out of stock in selected branch');
      }
    }
  };

  const updateCartItemPrice = (itemId: string, newPrice: number) => {
    setCart(cart.map(cartItem =>
      cartItem.id === itemId
        ? { ...cartItem, price: newPrice }
        : cartItem
    ));
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const updateCartQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    // Check against available quantity in selected branch
    const itemWithAvailability = itemsWithAvailability.find(i => i.id === itemId);
    const availableQty = itemWithAvailability?.availableQuantity || 0;
    
    if (quantity > availableQty) {
      toast.error(`Cannot add more items. Only ${availableQty} available in selected branch.`);
      return;
    }

    setCart(cart.map(cartItem =>
      cartItem.id === itemId
        ? { ...cartItem, quantity }
        : cartItem
    ));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.price.toString()) * item.quantity), 0);
  };

  const handleCreateLayaway = async () => {
    if (!customer.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (cart.length === 0) {
      toast.error('Please add items to the layaway');
      return;
    }

    if (initialPayment > cartTotal) {
      toast.error('Initial payment cannot exceed total amount');
      return;
    }

    // Validation for required fields
    if (!user?.id) {
      toast.error('User ID is required');
      return;
    }

    // Check for branch_id - it's required by the backend
    const branchId = isAdmin ? selectedBranch : user?.branch_id;
    if (!branchId) {
      toast.error('Please select a branch');
      return;
    }

    setIsCreating(true);
    try {
      const layawayData = {
        user_id: user.id,
        branch_id: branchId,
        customer_name: customer.name.trim(),
        customer_phone: customer.phone?.trim() || null,
        customer_address: customer.address?.trim() || null,
        total_amount: parseFloat(cartTotal.toFixed(2)),
        subtotal: parseFloat(cartTotal.toFixed(2)),
        product_handling: productHandling,
        payment_schedule_months: parseInt(paymentScheduleMonths.toString()),
        initial_payment: parseFloat(initialPayment.toFixed(2)),
        payment_method: paymentMethod,
        notes: notes?.trim() || null,
        // Store layaway date in configured system timezone
        layaway_date: formatInTimeZone(
          layawayDate,
          systemTimezone,
          "yyyy-MM-dd HH:mm:ss"
        ),
        items: cart.map(item => ({
          item_id: item.id.toString(),
          quantity: parseInt(item.quantity.toString()),
          price: parseFloat(item.price.toString()),
        })),
      };

      console.log('Layaway data being sent:', layawayData);

      await createLayaway(layawayData);
      toast.success('🎉 Layaway created successfully!');
      
      // Reset form
      setCart([]);
      setCustomer({ name: '', phone: '', address: '' });
      setProductHandling('hold');
      setPaymentScheduleMonths(1);
      setInitialPayment(0);
      setPaymentMethod('cash');
      setNotes('');
      setItemSearchTerm('');
      // Reset to system timezone date
      const now = toZonedTime(new Date(), systemTimezone);
      now.setHours(0, 0, 0, 0);
      setLayawayDate(now);
      setActiveTab('list');
      fetchLayawaysData();
    } catch (error) {
      console.error('Failed to create layaway:', error);
      toast.error('Failed to create layaway');
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewLayaway = async (layawayId: string) => {
    try {
      const response = await fetchLayaway(layawayId);
      setSelectedLayaway(response.data);
      setShowViewDialog(true);
    } catch (error) {
      console.error('Failed to fetch layaway details:', error);
      toast.error('Failed to fetch layaway details');
    }
  };

  const handleAddPayment = async () => {
    if (!selectedLayaway || !paymentAmount) {
      toast.error('Please enter a payment amount');
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (amount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }

    if (amount > selectedLayaway.remaining_amount) {
      toast.error('Payment amount cannot exceed remaining balance');
      return;
    }

    setIsAddingPayment(true);
    try {
      await addLayawayPayment(selectedLayaway.id, {
        user_id: user?.id,
        amount: amount,
        payment_method: paymentMethodDialog,
        notes: paymentNotes || null,
        payment_date: paymentDate,
      });

      const isFullyPaid = amount >= selectedLayaway.remaining_amount;
      toast.success(
        isFullyPaid 
          ? '🎉 Payment added! Layaway is now complete!' 
          : '💰 Payment added successfully'
      );
      
      setShowPaymentDialog(false);
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      fetchLayawaysData();
      
      if (showViewDialog) {
        handleViewLayaway(selectedLayaway.id);
      }
    } catch (error) {
      console.error('Failed to add payment:', error);
      toast.error('Failed to add payment');
    } finally {
      setIsAddingPayment(false);
    }
  };

  const handleCancelLayaway = async (layawayId: string) => {
    if (!confirm('Are you sure you want to cancel this layaway?')) return;

    try {
      await cancelLayaway(layawayId);
      toast.success('Layaway cancelled successfully');
      fetchLayawaysData();
      setShowViewDialog(false);
    } catch (error) {
      console.error('Failed to cancel layaway:', error);
      toast.error('Failed to cancel layaway');
    }
  };

  const handleGenerateInvoice = async (layaway: Layaway) => {
    try {
      const response = await fetchLayaway(layaway.id);
      setSelectedLayaway(response.data);
      setShowInvoiceDialog(true);
    } catch (error) {
      console.error('Failed to fetch layaway details for invoice:', error);
      toast.error('Failed to fetch layaway details for invoice');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-100 text-blue-800">Active</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Layaways
              </h1>
              <p className="text-slate-600">Manage partial payment sales</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
              <div className="flex items-center space-x-2 text-sm">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-slate-600">Active</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-slate-600">Completed</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-slate-600">Cancelled</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger 
              value="list" 
              className="flex items-center space-x-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <List className="h-4 w-4" />
              <span>Layaway List</span>
            </TabsTrigger>
            <TabsTrigger 
              value="create"
              className="flex items-center space-x-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Plus className="h-4 w-4" />
              <span>Create Layaway</span>
            </TabsTrigger>
          </TabsList>

        <TabsContent value="list">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search layaways..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Layaway #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Total Amount</TableHead>
                      <TableHead>Paid Amount</TableHead>
                      <TableHead>Remaining</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Handling</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : layaways.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center">
                          No layaways found
                        </TableCell>
                      </TableRow>
                    ) : (
                      layaways.map((layaway) => (
                        <TableRow key={layaway.id}>
                          <TableCell className="font-medium">
                            {layaway.layaway_number}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{layaway.customer_name}</div>
                              {layaway.customer_phone && (
                                <div className="text-sm text-gray-500">
                                  {layaway.customer_phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{currencySymbol}{layaway.total_amount.toFixed(2)}</TableCell>
                          <TableCell>{currencySymbol}{layaway.paid_amount.toFixed(2)}</TableCell>
                          <TableCell>{currencySymbol}{layaway.remaining_amount.toFixed(2)}</TableCell>
                          <TableCell>{getStatusBadge(layaway.status)}</TableCell>
                          <TableCell>
                            <Badge variant={layaway.product_handling === 'hold' ? 'secondary' : 'outline'}>
                              {layaway.product_handling === 'hold' ? 'Hold' : 'Handover'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatLayawayDate(layaway.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewLayaway(layaway.id)}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {layaway.status === 'active' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const response = await fetchLayaway(layaway.id);
                                      setEditingLayaway(response.data);
                                      setShowEditDialog(true);
                                    } catch (error) {
                                      console.error('Failed to fetch layaway for editing:', error);
                                      toast.error('Failed to fetch layaway details');
                                    }
                                  }}
                                  title="Edit Layaway"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleGenerateInvoice(layaway)}
                                title="Generate Invoice"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              {layaway.status === 'active' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedLayaway(layaway);
                                      setShowPaymentDialog(true);
                                    }}
                                  >
                                    <DollarSign className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCancelLayaway(layaway.id)}
                                  >
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {pagination.last_page > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} layaways
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: 1 }))}
                      disabled={pagination.current_page === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                      disabled={pagination.current_page === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm px-3 py-1 bg-gray-100 rounded">
                      Page {pagination.current_page} of {pagination.last_page}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                      disabled={pagination.current_page === pagination.last_page}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: pagination.last_page }))}
                      disabled={pagination.current_page === pagination.last_page}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="create">
          <CreateLayawayTab
            filteredAndSortedItems={filteredAndSortedItems}
            itemSearchTerm={itemSearchTerm}
            setItemSearchTerm={setItemSearchTerm}
            sortBy={sortBy}
            setSortBy={setSortBy}
            cart={cart}
            addToCart={addToCart}
            removeFromCart={removeFromCart}
            updateCartQuantity={updateCartQuantity}
            updateCartItemPrice={updateCartItemPrice}
            customer={customer}
            setCustomer={setCustomer}
            productHandling={productHandling}
            setProductHandling={setProductHandling}
            paymentScheduleMonths={paymentScheduleMonths}
            setPaymentScheduleMonths={setPaymentScheduleMonths}
            initialPayment={initialPayment}
            setInitialPayment={setInitialPayment}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            notes={notes}
            setNotes={setNotes}
            systemTimezone={systemTimezone}
            layawayDate={layawayDate}
            setLayawayDate={setLayawayDate}
            isAdmin={isAdmin}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            branches={branches}
            cartSubtotal={cartSubtotal}
            cartVAT={cartVAT}
            cartTotal={cartTotal}
            vatPercentage={vatPercentage}
            currencySymbol={currencySymbol}
            createLayaway={handleCreateLayaway}
            isCreating={isCreating}
          />
        </TabsContent>
        </Tabs>

        {/* View Layaway Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>
                Layaway #{selectedLayaway?.layaway_number} Details
              </DialogTitle>
            </DialogHeader>
            {selectedLayaway && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold mb-2">Customer Information</h3>
                    <p><strong>Name:</strong> {selectedLayaway.customer_name}</p>
                    {selectedLayaway.customer_phone && (
                      <p><strong>Phone:</strong> {selectedLayaway.customer_phone}</p>
                    )}
                    {selectedLayaway.customer_address && (
                      <p><strong>Address:</strong> {selectedLayaway.customer_address}</p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Layaway Details</h3>
                    <p><strong>Status:</strong> {getStatusBadge(selectedLayaway.status)}</p>
                    <p><strong>Product Handling:</strong> {selectedLayaway.product_handling}</p>
                    <p><strong>Payment Schedule:</strong> {selectedLayaway.payment_schedule_months} months</p>
                  <p><strong>Created:</strong> {formatLayawayDate(selectedLayaway.created_at)}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Items</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedLayaway.items?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={parseFloat(item.price.toString()).toFixed(2)}
                              onChange={(e) => updateCartItemPrice(item.id, parseFloat(e.target.value) || 0)}
                              className="w-20 text-sm"
                            />
                          </TableCell>
                          <TableCell>{currencySymbol}{(item.quantity * parseFloat(item.price.toString())).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{currencySymbol}{selectedLayaway.total_amount.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Total Amount</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{currencySymbol}{selectedLayaway.paid_amount.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Paid Amount</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{currencySymbol}{selectedLayaway.remaining_amount.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Remaining Amount</div>
                  </div>
                </div>

                {selectedLayaway.payments && selectedLayaway.payments.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Payment History</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedLayaway.payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                            <TableCell>{currencySymbol}{payment.amount.toFixed(2)}</TableCell>
                            <TableCell className="capitalize">{payment.payment_method.replace('_', ' ')}</TableCell>
                            <TableCell>{payment.notes || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewDialog(false);
                      setShowInvoiceDialog(true);
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Invoice
                  </Button>
                  {selectedLayaway.status === 'active' && (
                    <>
                      <Button
                        onClick={() => {
                          setShowPaymentDialog(true);
                          setShowViewDialog(false);
                        }}
                      >
                        Add Payment
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleCancelLayaway(selectedLayaway.id)}
                      >
                        Cancel Layaway
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Payment Dialog - Enhanced */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-xl">Add Payment</DialogTitle>
                  <p className="text-sm text-slate-600 mt-1">Record a new payment for this layaway</p>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-6">
              {selectedLayaway && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Layaway #</span>
                      <span className="text-sm font-bold text-blue-600">#{selectedLayaway.layaway_number}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Customer</span>
                      <span className="text-sm text-slate-800">{selectedLayaway.customer_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Total Amount</span>
                      <span className="text-sm text-slate-800">{currencySymbol}{selectedLayaway.total_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Paid Amount</span>
                      <span className="text-sm text-green-600">{currencySymbol}{selectedLayaway.paid_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-blue-200 pt-2">
                      <span className="font-semibold text-slate-700">Remaining Balance</span>
                      <span className="font-bold text-lg text-orange-600">{currencySymbol}{selectedLayaway.remaining_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="pl-10 text-lg font-semibold bg-white border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    max={selectedLayaway?.remaining_amount}
                    step="0.01"
                  />
                </div>
                {paymentAmount && selectedLayaway && parseFloat(paymentAmount) > selectedLayaway.remaining_amount && (
                  <p className="text-red-500 text-xs mt-1">Amount cannot exceed remaining balance</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Date</label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="bg-white border-slate-200 focus:border-green-500"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Method</label>
                <Select value={paymentMethodDialog} onValueChange={setPaymentMethodDialog}>
                  <SelectTrigger className="bg-white border-slate-200 focus:border-green-500">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">
                      <div className="flex items-center space-x-2">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span>Cash</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="bank_transfer">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        <span>Bank Transfer</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="credit_card">
                      <div className="flex items-center space-x-2">
                        <CreditCard className="h-4 w-4 text-purple-600" />
                        <span>Credit Card</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="debit_card">
                      <div className="flex items-center space-x-2">
                        <CreditCard className="h-4 w-4 text-indigo-600" />
                        <span>Debit Card</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Notes (Optional)</label>
                <Input
                  placeholder="Add payment notes..."
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="bg-white border-slate-200 focus:border-green-500"
                />
              </div>

              {/* Payment Preview */}
              {paymentAmount && selectedLayaway && parseFloat(paymentAmount) > 0 && parseFloat(paymentAmount) <= selectedLayaway.remaining_amount && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <div className="text-sm text-green-800">
                    <div className="font-medium mb-2">Payment Preview</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>New Payment: {currencySymbol}{parseFloat(paymentAmount).toFixed(2)}</div>
                      <div>New Balance: {currencySymbol}{(selectedLayaway.remaining_amount - parseFloat(paymentAmount)).toFixed(2)}</div>
                      <div className="col-span-2 mt-1 pt-1 border-t border-green-300">
                        {selectedLayaway.remaining_amount - parseFloat(paymentAmount) <= 0 
                          ? <span className="font-medium text-green-700">🎉 Layaway will be completed!</span>
                          : <span className="text-green-700">Layaway will remain active</span>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowPaymentDialog(false)}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddPayment}
                  disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || (selectedLayaway && parseFloat(paymentAmount) > selectedLayaway.remaining_amount) || isAddingPayment}
                  className="px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  {isAddingPayment ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4" />
                      <span>Add Payment</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invoice Dialog */}
        <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Layaway Invoice</DialogTitle>
            </DialogHeader>
            {selectedLayaway && (
              <LayawayInvoice
                layaway={selectedLayaway}
                currencySymbol={currencySymbol}
                vatPercentage={vatPercentage}
                branchName={branches.find(b => b.id === selectedLayaway.branch_id)?.name || ''}
                companyDetails={{
                  name: companySettings.company_name,
                  address: companySettings.address,
                  phone: companySettings.phone,
                  email: companySettings.email
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Layaway Dialog */}
        <EditLayawayDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          layaway={editingLayaway}
          items={items}
          inventory={inventory}
          branches={branches}
          vatPercentage={vatPercentage}
          currencySymbol={currencySymbol}
          onSuccess={() => {
            fetchLayawaysData();
            setEditingLayaway(null);
          }}
        />
      </div>
    </div>
  );
};

export default Layaways;
